import type {Editor, Keymap, KeymapOptions} from 'grapesjs';
import {EMAIL_MAKER_COMMANDS} from '@EmailMakerReact/email-maker-react.constants';
import {cutSelectedComponents, duplicateSelectedComponent, toggleComponentLock} from '@EmailMakerReact/lib/email-maker-clipboard-library';
import {isMultiselect} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {alignSelectedText, toggleBold, toggleItalic, toggleStrikethrough, toggleUnderline} from '@EmailMakerReact/lib/email-maker-component-style-library';
import {selectNextItem, selectPreviousItem, unselectComponent} from '@EmailMakerReact/lib/email-maker-selection-library';
import {fitToScreen, zoomIn, zoomOut} from '@EmailMakerReact/lib/email-maker-zoom-library';

interface Keybinding {
  keys: string;
  handler: Keymap['handler'];
  /** When false, the handler is wrapped to no-op while more than one component is selected. */
  isMultiSelectSupported?: boolean;
  opts?: KeymapOptions;
}

/**
 * Keymap ids after which the style manager is refreshed. Consumed by editor.ts's `keymap:emit`
 * listener; exported here so the ids stay coupled to the keybindings registered below.
 */
export const REFRESH_STYLES_AFTER_KEYMAPS: string[] = [
  'text:align-left',
  'text:align-center',
  'text:align-right',
  'text:align-justify',
  'text:toggle-bold',
  'text:toggle-italic',
  'text:toggle-underline',
  'text:toggle-strikethrough',
  'component:lock',
];

export function initKeymapListeners(editor: Editor): () => void {
  const keymaps = editor.Keymaps;

  const keybindings: Record<string, Keybinding> = {
    'core:undo': {
      keys: '⌘+z, ctrl+z',
      handler: 'core:undo',
    },
    'core:redo': {
      keys: '⌘+shift+z, ctrl+shift+z',
      handler: 'core:redo',
    },
    'component:copy': {
      keys: '⌘+c, ctrl+c',
      handler: EMAIL_MAKER_COMMANDS.CORE_COMPONENT_COPY,
    },
    'component:paste': {
      keys: '⌘+v, ctrl+v',
      handler: EMAIL_MAKER_COMMANDS.CORE_COMPONENT_PASTE,
    },
    'component:cut': {
      keys: '⌘+x, ctrl+x',
      handler: (): void => {
        cutSelectedComponents(editor);
      },
    },
    'component:delete': {
      keys: 'backspace, delete',
      handler: EMAIL_MAKER_COMMANDS.REMOVE_COMPONENT,
    },
    'component:duplicate': {
      keys: '⌘+d, ctrl+d',
      handler: (): void => {
        void duplicateSelectedComponent(editor);
      },
      opts: {
        prevent: true,
      },
    },
    'component:lock': {
      keys: '⌘+l, ctrl+l',
      handler: (): void => {
        toggleComponentLock(editor);
      },
      opts: {
        prevent: true,
      },
    },
    'component:next-item': {
      keys: 'tab',
      handler: (): void => {
        selectNextItem(editor);
      },
      opts: {
        prevent: true,
      },
    },
    'component:previous-item': {
      keys: 'shift+tab',
      handler: (): void => {
        selectPreviousItem(editor);
      },
      opts: {
        prevent: true,
      },
    },
    'component:unselect': {
      keys: 'esc',
      handler: (): void => {
        unselectComponent(editor);
      },
    },
    'text:align-left': {
      keys: '⌘+shift+l, ctrl+shift+l',
      isMultiSelectSupported: false,
      handler: (): void => {
        alignSelectedText(editor, 'left');
      },
    },
    'text:align-center': {
      keys: '⌘+shift+c, ctrl+shift+c',
      isMultiSelectSupported: false,
      handler: (): void => {
        alignSelectedText(editor, 'center');
      },
      opts: {
        prevent: true,
      },
    },
    'text:align-right': {
      keys: '⌘+shift+r, ctrl+shift+r',
      isMultiSelectSupported: false,
      handler: (): void => {
        alignSelectedText(editor, 'right');
      },
      opts: {
        prevent: true,
      },
    },
    'text:align-justify': {
      keys: '⌘+shift+j, ctrl+shift+j',
      isMultiSelectSupported: false,
      handler: (): void => {
        alignSelectedText(editor, 'justify');
      },
      opts: {
        prevent: true,
      },
    },
    'text:toggle-bold': {
      keys: '⌘+b, ctrl+b',
      isMultiSelectSupported: false,
      handler: (): void => {
        toggleBold(editor);
      },
    },
    'text:toggle-italic': {
      keys: '⌘+i, ctrl+i',
      isMultiSelectSupported: false,
      handler: (): void => {
        toggleItalic(editor);
      },
    },
    'text:toggle-underline': {
      keys: '⌘+u, ctrl+u',
      isMultiSelectSupported: false,
      handler: (): void => {
        toggleUnderline(editor);
      },
    },
    'text:toggle-strikethrough': {
      keys: '⌘+s, ctrl+s',
      isMultiSelectSupported: false,
      handler: (): void => {
        toggleStrikethrough(editor);
      },
      opts: {
        prevent: true,
      },
    },
    'core:component-next': {
      keys: 'd',
      handler: 'core:component-next',
    },
    'core:component-prev': {
      keys: 'a',
      handler: 'core:component-prev',
    },
    'core:component-enter': {
      keys: 's',
      handler: 'core:component-enter',
    },
    'core:component-exit': {
      keys: 'w',
      handler: 'core:component-exit',
    },
    'window:zoom-in': {
      keys: '+, =',
      handler: (): void => {
        zoomIn(editor);
      },
    },
    'window:zoom-out': {
      keys: '-',
      handler: (): void => {
        zoomOut(editor);
      },
    },
    'window:fit-to-screen': {
      keys: '⌘+0, ctrl+0',
      handler: (): void => {
        fitToScreen(editor);
      },
    },
  };

  const keymapIds = Object.keys(keybindings);

  keymapIds.forEach((keymapId: string) => {
    const keybinding = keybindings[keymapId];
    const handler = keybinding.isMultiSelectSupported === false ? getHandlerForMultiSelect(editor, keybinding.handler) : keybinding.handler;

    keymaps.add(keymapId, keybinding.keys, handler, keybinding.opts);
  });

  return (): void => {
    keymapIds.forEach((keymapId: string) => {
      keymaps.remove(keymapId);
    });
  };
}

/** Keymaps flagged isMultiSelectSupported=false no-op while a multi-selection is active. */
function getHandlerForMultiSelect(editor: Editor, handler: Keymap['handler']): () => void {
  return (): void => {
    if (isMultiselect(editor)) {
      return;
    }

    if (typeof handler === 'string') {
      editor.runCommand(handler);
      return;
    }

    handler(editor, undefined, {});
  };
}
